import { useCallback, useEffect, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import { parseAllDocuments as yamlLoad } from "yaml";

import type {
  CompleteRuleConfig,
  MatchEnvMap,
  Node,
  WasmLang,
} from "@acme/ast-grep-wasm-loader";
import type {
  AstGrepMatch,
  StudioState,
  SupportedLang,
} from "@acme/validators";
import { getAstGrepMatchesHash } from "@acme/shared";
import { StudioMode } from "@acme/validators";

import type { WorkerLog } from "./_utils/codemod-sandbox-worker";
import { useDebounceWatch } from "~/hooks/use-debounce-watch";
import { useTRPC } from "~/trpc/react";
import {
  dumpASTNodes,
  executeCode,
  isSkippedCallError,
  processRuleConfig,
  terminateWorker,
} from "./_utils/codemod-sandbox-executor";
import { useCurrentSource, useStudioStore } from "./use-studio-store";

export function buildRules(state: Pick<StudioState, "config" | "lang">) {
  const rules: CompleteRuleConfig[] = [];
  if (state.config) {
    const ruleStr = state.config;
    const json = yamlLoad(ruleStr, {});
    let i = 0;
    for (const doc of json) {
      const rule = doc.toJSON() as CompleteRuleConfig | null | string;
      if (!rule || typeof rule !== "object") {
        continue;
      }
      if (!rule.id) {
        rule.id = `test-rule-${i++}`;
      }
      if (state.lang === "c_sharp") {
        rule.language = "csharp";
      } else {
        rule.language = state.lang as WasmLang;
      }
      rules.push(rule);
    }
  }
  return rules;
}

export function useInitializeSharedSession() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const shareId = searchParams.get("share_id");
  const loadSessionState = useStudioStore((state) => state.loadSessionState);

  const api = useTRPC();

  // Fetch shared session by shareId
  const {
    data: sharedSessionData,
    isLoading: isLoadingSharedSession,
    error: sharedSessionError,
  } = useQuery(
    api.sharedAstGrepStudioSession.getByShareId.queryOptions(
      { shareId: shareId ?? "" },
      { enabled: !!shareId },
    ),
  );

  // Load shared session when it's fetched
  useEffect(() => {
    if (sharedSessionData) {
      // Clone and ensure lang is of the correct type
      const state = { ...sharedSessionData.state };

      loadSessionState({
        ...state,
        lang: sharedSessionData.lang as SupportedLang,
      });

      // Remove share_id from URL but keep the history
      if (shareId) {
        const newUrl = window.location.pathname;
        router.replace(newUrl);
      }
    }
  }, [sharedSessionData, shareId, loadSessionState, router]);

  return {
    isLoadingSharedSession,
    sharedSessionError,
  };
}

export function useAstGrep() {
  const currentSource = useCurrentSource()?.source ?? "";
  const setRuleErrors = useStudioStore((s) => s.setRuleErrors);
  const clearRuleErrors = useStudioStore((s) => s.clearRuleErrors);
  const [logs, setLogs] = useState<WorkerLog[]>([]);

  const handleClearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  const [matchedHighlights, setMatchedHighlights] = useState<
    AstGrepMatch<MatchEnvMap>[]
  >([]);

  const [rewrittenCode, setRewrittenCode] = useState<{
    sourceCode: string;
    updatedCode: string;
  }>({
    sourceCode: currentSource,
    updatedCode: currentSource,
  });

  const handleCodeChange = useCallback(
    (sourceCode: string, updatedCode: string) => {
      setRewrittenCode((prev) => {
        if (prev.updatedCode === updatedCode && prev.sourceCode === sourceCode)
          return prev;
        return {
          sourceCode,
          updatedCode,
        };
      });
    },
    [],
  );

  const abortControllerRef = useRef<AbortController | null>(null);
  const previousMatchesHash = useRef<string>("");

  const handleMatchesChange = useCallback(
    (matches: AstGrepMatch<MatchEnvMap>[]) => {
      const currentMatchesHash = getAstGrepMatchesHash(matches);
      if (currentMatchesHash !== previousMatchesHash.current) {
        previousMatchesHash.current = currentMatchesHash;
        setMatchedHighlights(matches);
      }
    },
    [],
  );

  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const initialState = useStudioStore.getState();

    let lang = initialState.lang;
    let mode = initialState.mode;
    let config = initialState.config;
    let jsAstGrepCodemod = initialState.jsAstGrepCodemod;

    const unsubscribe = useStudioStore.subscribe((state) => {
      if (
        state.lang !== lang ||
        state.mode !== mode ||
        state.config !== config ||
        state.jsAstGrepCodemod !== jsAstGrepCodemod
      ) {
        lang = state.lang;
        mode = state.mode;
        config = state.config;
        jsAstGrepCodemod = state.jsAstGrepCodemod;
        void processRules();
      }
    });

    const processRules = async () => {
      if (
        abortControllerRef.current &&
        !abortControllerRef.current.signal.aborted
      ) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      const abortController = abortControllerRef.current;
      try {
        switch (mode) {
          case StudioMode.Config: {
            const json = buildRules({
              config,
              lang,
            });
            try {
              const result = await processRuleConfig(
                currentSource,
                json,
                lang as WasmLang,
              );
              if (abortController.signal.aborted) return;
              handleCodeChange(currentSource, result.fixed);
              handleMatchesChange(result.matches);
              clearRuleErrors();
            } catch (error) {
              if (!isSkippedCallError(error)) {
                console.error(error);
                setRuleErrors(String(error));
              }
            }

            break;
          }
          case StudioMode.JsAstGrep: {
            const { result: updatedCode, logs } = await executeCode(
              jsAstGrepCodemod,
              currentSource,
              lang as WasmLang,
            );
            if (abortController.signal.aborted) return;
            if (typeof updatedCode !== "string" && updatedCode != null) {
              setRuleErrors(
                `The output of the JS function must be a string or null or undefined. Received: ${typeof updatedCode}`,
              );
              return;
            }

            setLogs(logs);
            clearRuleErrors();
            handleCodeChange(currentSource, updatedCode ?? currentSource);
            handleMatchesChange([]);
            break;
          }
        }
      } catch (error: unknown) {
        if (!isSkippedCallError(error)) {
          console.warn(error);
          const message =
            "message" in (error as Error)
              ? String((error as Error).message)
              : "message" in (error as { cause: Error }).cause
                ? String((error as { cause: Error }).cause.message)
                : String(error);
          setRuleErrors(message);
          handleMatchesChange([]);
        }
      }
    };

    debounceTimeout.current = setTimeout(() => {
      void processRules();
    }, 1);

    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
      abortControllerRef.current?.abort();
      unsubscribe();
      terminateWorker();
    };
  }, [
    clearRuleErrors,
    currentSource,
    handleCodeChange,
    handleMatchesChange,
    setRuleErrors,
  ]);

  return {
    matchedHighlights,
    rewrittenCode,
    logs,
    handleClearLogs,
  };
}

export function useDumpASTNodes() {
  const lang = useStudioStore((s) => s.lang);
  const [astNodes, setAstNodes] = useState<Node | null>(null);
  const source = useCurrentSource();

  const debouncedSource = useDebounceWatch(source?.source ?? "", 150);

  useEffect(() => {
    void dumpASTNodes(lang as WasmLang, debouncedSource)
      .then((result) => {
        setAstNodes(result);
      })
      .catch((error) => {
        if (!isSkippedCallError(error)) {
          console.warn(error);
        }
      });
  }, [debouncedSource, lang]);

  return astNodes;
}
